// components/turnier/AusschreibungVorlagen.jsx — Vorlagen je Verband für
// Haftung, Datenschutz und Versicherung (22.10.2026, Entscheidung des
// Inhabers: je Verband vorbelegt, je Turnier änderbar).
//
// Verbands-Admin: immer der eigene Verband (der Server nimmt ihn aus dem
// Token, eine andere Nummer wird nicht gelesen). Plattform-Admin: wählt.
// Übernommen wird eine Vorlage im Turnier über „Aus Verbandsvorlage
// übernehmen“ (Reiter/Schritt Ausschreibung), nie still.
import React, { useCallback, useEffect, useState } from 'react';
import '../../styles/TurnierAblauf.css';
import './AusschreibungEditor.css';

const kopf = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
async function antwort(res) {
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.success === false) throw new Error(d.error || `Fehler ${res.status}`);
  return d;
}
const istPlattform = () => {
  try { const u = JSON.parse(localStorage.getItem('adminData') || 'null')?.user; return !!u && u.verband_id_tenant == null; } catch { return false; }
};

// Eingebettet (Verwaltung › Turnier-Einstellungen, 23.10.2026): die Seite lädt
// und speichert selbst, zusammen mit ihren anderen Bereichen, und warnt vor
// ungespeicherten Änderungen. Dann kommt `gesteuert` mit
//   { werte, verbandName, feld, onFeld(k, text), verbaende?, verband?, onVerband?, nurLesen? }
// und hier steht nur das Eingabefeld (bzw. alle drei, wenn `feld` fehlt).
const FELDER = [['haftung_text', 'Haftung'], ['versicherung_text', 'Versicherung'], ['datenschutz_text', 'Datenschutz und Fotos']];
function Gesteuert({ g }) {
  const felder = g.feld ? FELDER.filter(([k]) => k === g.feld) : FELDER;
  return (
    <div className="au-ed te-vorlage">
      {g.verbaende && (
        <label className="tw-feld tw-schmal">Verband
          <select value={g.verband || ''} onChange={(e) => g.onVerband(e.target.value)}>
            {g.verbaende.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
      )}
      {felder.map(([k, label]) => (
        <label key={k} className="tw-feld tw-breit">{g.feld ? `Text des Verbands ${g.verbandName || ''}`.trim() : label}
          <textarea rows={8} value={g.werte?.[k] || ''} readOnly={!!g.nurLesen}
            placeholder="Noch kein Text. Der Schritt „Ausschreibung“ jedes Turniers schlägt ihn vor."
            onChange={(e) => g.onFeld(k, e.target.value)} />
        </label>
      ))}
      <p className="ta-klein">Ändert keine bestehenden Turniere. Im Turnier: „Aus Verbandsvorlage übernehmen“ (Schritt Ausschreibung).</p>
    </div>
  );
}

export default function AusschreibungVorlagen({ gesteuert = null }) {
  if (gesteuert) return <Gesteuert g={gesteuert} />;
  return <AusschreibungVorlagenFrei />;
}

function AusschreibungVorlagenFrei() {
  const plattform = istPlattform();
  const [verbaende, setVerbaende] = useState([]);
  const [verband, setVerband] = useState('');
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [hinweis, setHinweis] = useState('');

  useEffect(() => {
    if (!plattform) return;
    fetch('/api/verbaende', { headers: kopf() }).then(antwort)
      .then((d) => { const l = d.data || d.verbaende || []; setVerbaende(l); if (l[0]) setVerband(String(l[0].id)); })
      .catch((e) => setFehler(e.message));
  }, [plattform]);

  const laden = useCallback(async () => {
    if (plattform && !verband) return;
    setFehler(''); setHinweis('');
    try {
      const d = await antwort(await fetch(`/api/ausschreibung/vorlagen${plattform ? `?verband_id=${verband}` : ''}`, { headers: kopf() }));
      setV(d.data);
    } catch (e) { setFehler(e.message); }
  }, [plattform, verband]);
  useEffect(() => { laden(); }, [laden]);

  const speichern = async () => {
    setBusy(true); setFehler(''); setHinweis('');
    try {
      const d = await antwort(await fetch('/api/ausschreibung/vorlagen', {
        method: 'PUT', headers: kopf(),
        body: JSON.stringify({ haftung_text: v.haftung_text, datenschutz_text: v.datenschutz_text, versicherung_text: v.versicherung_text, ...(plattform ? { verband_id: Number(verband) } : {}) }),
      }));
      setV(d.data);
      setHinweis('Vorlagen gespeichert. Bestehende Turniere ändern sich dadurch nicht.');
    } catch (e) { setFehler(e.message); } finally { setBusy(false); }
  };

  const feld = (k, label) => (
    <label className="tw-feld tw-breit">{label}
      <textarea rows={4} value={v?.[k] || ''} onChange={(e) => setV((x) => ({ ...x, [k]: e.target.value }))} />
    </label>
  );

  return (
    <div className="au-ed" style={{ maxWidth: 860 }}>
      <p className="ta-klein">Diese Texte schlägt der Schritt „Ausschreibung“ jedes Turniers vor (Knopf „Aus Verbandsvorlage übernehmen“). Im Turnier bleiben sie änderbar; eine Änderung hier ändert keine bestehenden Turniere.</p>
      {plattform && (
        <label className="tw-feld tw-schmal">Verband
          <select value={verband} onChange={(e) => setVerband(e.target.value)}>
            {verbaende.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
      )}
      {v && <>
        <h3>{v.verband_name}</h3>
        <div className="tw-raster">
          {feld('haftung_text', 'Haftung')}
          {feld('versicherung_text', 'Versicherung')}
          {feld('datenschutz_text', 'Datenschutz und Fotos')}
        </div>
        <div className="ta-knoepfe tw-fuss">
          <button type="button" className="ta-btn ta-btn-haupt" disabled={busy} onClick={speichern}>{busy ? 'Speichert …' : 'Vorlagen speichern'}</button>
        </div>
      </>}
      {fehler && <div className="ta-meldung ta-meldung-fehler" role="alert">{fehler}</div>}
      {hinweis && <div className="ta-meldung ta-meldung-ok" role="status">{hinweis}</div>}
    </div>
  );
}
